/**
 * Tâche 1425 (Eric 2026-10-01) — Le sondage épinglé, en haut du canal.
 *
 * Un seul épinglé par canal, et c'est structurant : trois bandeaux empilés
 * ne mettent plus rien en avant, ils repoussent la conversation hors de
 * l'écran.
 *
 * Il se recharge quand les messages changent (`cle`) : voter dans le fil doit
 * se refléter en haut, sinon le bandeau affirme encore « tu n'as pas voté »
 * juste après qu'on ait voté.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { PushPin } from "@phosphor-icons/react";

import { API_BASE as API } from "../../lib/apiBase";
import { useLang } from "../../contexts/LanguageContext";
import PollCard from "./PollCard";

export default function PinnedPoll({ channelId, conversationId, currentUserId, canAdmin, cle }) {
  const { tr } = useLang();
  const [sondage, setSondage] = useState(null);

  const charger = useCallback(async () => {
    if (!channelId && !conversationId) return;
    const params = channelId ? { channel_id: channelId } : { conversation_id: conversationId };
    try {
      const { data } = await axios.get(`${API}/api/polls/pinned`, { params, withCredentials: true });
      setSondage(data.poll || null);
    } catch {
      // Un bandeau absent est préférable à un bandeau faux : on ne garde pas
      // l'épinglé précédent après un échec de lecture.
      setSondage(null);
    }
  }, [channelId, conversationId]);

  useEffect(() => {
    charger();
    // Rechargement périodique, en plus de `cle` : un sondage épinglé PAR
    // QUELQU'UN D'AUTRE doit apparaître sans recharger la page — et c'est
    // aussi ce qui fait apparaître le bandeau juste après qu'on l'ait
    // épinglé soi-même, puisque le nombre de messages n'a alors pas bougé.
    const t = setInterval(charger, 15000);
    return () => clearInterval(t);
  }, [charger, cle]);

  if (!sondage) return null;

  // Tâche 1462 (Eric 2026-06) — « le sondage épinglé QUI ATTEND ENCORE DES
  // RÉPONSES, pour qu'il ne se perde pas ». Le bandeau existait (1425) mais
  // ne disait pas s'il attendait quelque chose : épinglé et déjà répondu par
  // tout le monde, il occupait le haut de l'écran sans rien demander, et on
  // finissait par ne plus le voir du tout.
  // ⚠️ Il reste affiché même quand on a répondu : un épinglé est structurant,
  // et le faire disparaître après SON vote empêcherait de suivre les
  // réponses des autres. Ce qui change, c'est le ton et ce qu'il annonce.
  const p = sondage.poll || {};
  const mAttend = !!p.m_attend;
  const enAttente = p.nb_en_attente || 0;

  return (
    <div
      className={`border-b px-4 py-2 ${mAttend
        ? "border-amber-300 bg-amber-50/70"
        : "border-indigo-200 bg-indigo-50/40"}`}
      data-testid="pinned-poll"
    >
      <p className={`mb-1.5 flex flex-wrap items-center gap-1 text-[10px] font-semibold uppercase tracking-wide ${
        mAttend ? "text-amber-800" : "text-indigo-700"}`}>
        <PushPin size={11} weight="fill" />
        {tr("Sondage épinglé", "Pinned poll")}
        {mAttend ? (
          <span data-testid="pinned-poll-attend-moi">
            · {tr("il attend ta réponse", "it is waiting for your answer")}
          </span>
        ) : enAttente > 0 ? (
          <span className="text-indigo-500" data-testid="pinned-poll-attend-autres">
            · {tr(`tu as répondu · ${enAttente} personne(s) n'ont pas encore répondu`,
                  `you answered · ${enAttente} person(s) have not answered yet`)}
          </span>
        ) : (
          <span className="text-indigo-500" data-testid="pinned-poll-complet">
            · {tr("tout le monde a répondu", "everyone answered")}
          </span>
        )}
      </p>
      <PollCard
        message={sondage}
        currentUserId={currentUserId}
        canAdmin={canAdmin}
        compact
        onChanged={(maj) => setSondage((p) => ({ ...p, ...maj }))}
      />
    </div>
  );
}
